//Libraries
import { useRef, useState } from 'react';
import { Button, EyeClosed, Field, IconButton, ViewEye } from '@effizency/efz-design-system';
import { useZodForm } from '@effizency/efz-toolkit/hooks';
import { Controller } from 'react-hook-form';
import { useNavigate } from 'react-router';

//Components
import IntlMessages from 'components/util/IntlMessages';

//Constants
import { BASENAME_URL } from 'constants/settings';

//Hooks
import useAuth from 'hooks/auth/useAuth';

//Styles
import { StyledLoginForm, StyledLoginFormFieldGroup } from 'pages/auth/Login/index.styled';

//Schemas
import { loginFormSchema } from 'schemas/forms/auth';

//Services
import { getFormErrorMessage, intlMessages, isFieldDefined } from 'services/util/auxiliaryUtils';

import type { TLoginFormValues } from 'types/forms/auth';

const LoginForm = () => {
    const navigate = useNavigate();

    // The form's schema depends on useAuth (the password is only asked for when the user does not sign in through
    // SSO), so useAuth reaches the form's setError through this ref when the API refuses the username or password.
    const showErrorRef = useRef<(name: 'username' | 'password', messageId: string) => void>(() => {});
    const { requesting, showPassword, login, checkSSOUser, handleChangeUsername } = useAuth({
        setUsernameError: (messageId) => showErrorRef.current('username', messageId),
        setPasswordError: (messageId) => showErrorRef.current('password', messageId),
    });

    const { handleSubmit, control, formState, setError, reset, getValues } = useZodForm({
        schema: loginFormSchema(showPassword),
        mode: 'onChange',
        defaultValues: {
            username: '',
            password: '',
        },
    });
    const { errors, isValid } = formState;
    showErrorRef.current = (name, messageId) => setError(name, { message: messageId });

    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    const onSubmit = ({ username, password }: TLoginFormValues) => {
        if (showPassword) {
            login({ username, password });
        } else {
            checkSSOUser(username);
        }
    };

    const handleChangeUsernameClick = () => {
        handleChangeUsername();
        reset({ username: getValues('username'), password: '' });
    };

    return (
        <StyledLoginForm autoComplete={'true'} onSubmit={handleSubmit(onSubmit)}>
            {/* username */}
            <StyledLoginFormFieldGroup>
                <Controller
                    name="username"
                    control={control}
                    render={({ field: { ref: _ref, ...field } }) => (
                        <Field
                            {...field}
                            value={field.value ?? ''}
                            size="md"
                            maxWidth="full"
                            label={<IntlMessages id="label.username" />}
                            required
                            disabled={requesting || showPassword}
                            error={!!errors?.username}
                            errorMessage={getFormErrorMessage(errors?.username)}
                            placeholder={intlMessages('label.username')}
                            dataTestId="login-username-id"
                        />
                    )}
                />

                {showPassword && (
                    <Button variant="tertiary" size="sm" onClick={handleChangeUsernameClick} dataTestId="login-change-username-id">
                        <IntlMessages id="label.change" />
                    </Button>
                )}
            </StyledLoginFormFieldGroup>

            {/* password */}
            {showPassword && (
                <StyledLoginFormFieldGroup>
                    <Controller
                        name="password"
                        control={control}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <Field
                                {...field}
                                value={field.value ?? ''}
                                type={isPasswordVisible ? 'text' : 'password'}
                                size="md"
                                maxWidth="full"
                                label={<IntlMessages id="label.password" />}
                                required
                                disabled={requesting}
                                error={!!errors?.password}
                                errorMessage={getFormErrorMessage(errors?.password)}
                                placeholder={intlMessages('label.password')}
                                dataTestId="login-password-id"
                                endAdornment={
                                    isFieldDefined(field.value) && (
                                        <IconButton
                                            variant="tertiary"
                                            type="button"
                                            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                                            dataTestId={
                                                isPasswordVisible ? 'login-password-id-hide-password' : 'login-password-id-show-password'
                                            }
                                        >
                                            {isPasswordVisible ?
                                                <ViewEye />
                                            :   <EyeClosed />}
                                        </IconButton>
                                    )
                                }
                            />
                        )}
                    />

                    <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => navigate(`${BASENAME_URL}reset-password`)}
                        dataTestId="login-forget-password-id"
                    >
                        <IntlMessages id="page.login.forgetPassword" />
                    </Button>
                </StyledLoginFormFieldGroup>
            )}

            {/*actions*/}
            <Button size="md" disabled={requesting || !isValid} loading={requesting} type="submit" dataTestId="login-submit-id">
                <IntlMessages id={'label.signin'} />
            </Button>
        </StyledLoginForm>
    );
};

export default LoginForm;
